import {
  gql,
  useQuery,
  useReactiveVar,
  WatchQueryFetchPolicy,
} from '@apollo/client';
import { showToast } from 'components/common/CustomToast';
import AccountContext from 'context/AccountContext';
import { READ_AVAILABLE_SUBSCRIPTIONS } from 'graphql/queries/readAvailableSubscriptions';
import { useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { featureCountryCode } from 'states/persistInStorage';
import { useSubscriptionStore } from 'stores/subscriptionStore';
import {
  Pmc,
  Query,
  QueryReadAvailableSubscriptionsArgs,
} from 'types/generatedSchemaTypes';
import { updateCache } from 'utils/cacheUtils';
import { getBillingType, getSubscriptionData } from 'utils/permitUtils';
import useCreateSalesOrder from './useCreateSalesOrder';
import useUpdateSalesOrder from './useUpdateSalesOrder';

/**
 * Full subscription hook with action methods (purchase, update).
 * Gets customer subscription from context (fetched by AccountDetailsProvider).
 * Only fetches available subscriptions for non-subscribers.
 */
const useSubscriptions = (
  fetchPolicy: WatchQueryFetchPolicy = 'cache-and-network'
) => {
  const { t } = useTranslation();
  const {
    customer,
    customerSubscription,
    customerSubscriptionLoading,
    refetchCustomerSubscription,
  } = useContext(AccountContext);
  const appCountry = useReactiveVar(featureCountryCode);
  const { setIsRecurring, setNextBillingDate } = useSubscriptionStore();
  const { createDraftSalesOrder, loadingSalesOrder } = useCreateSalesOrder();

  // Only fetch available subscriptions for non-subscribers
  const {
    data: availableSubscriptions,
    loading: loadingAvailableSubscriptions,
  } = useQuery<Query, QueryReadAvailableSubscriptionsArgs>(
    READ_AVAILABLE_SUBSCRIPTIONS,
    {
      variables: {
        input: {
          countryCode: appCountry,
          permitType: 'AIMO_PLUS',
        },
      },
      fetchPolicy: fetchPolicy,
      skip: !!customer.aimoPlusSubscriber,
      errorPolicy: 'all',
    }
  );

  // For now we only have one subscription per country available, so we take the first from the list
  const availableSubscription =
    availableSubscriptions?.readAvailableSubscriptions?.[0] ?? null;

  // Use customer subscription from context, or available subscription for non-subscribers
  const subscriptionData = useMemo(
    () => customerSubscription ?? getSubscriptionData(availableSubscription),
    [customerSubscription, availableSubscription]
  );

  // Now use useUpdateSalesOrder with access to subscriptionData
  const { updateSalesOrder, loading: updateLoading } = useUpdateSalesOrder({
    customUpdate: (cache, { data }) => {
      const salesOrderId = subscriptionData?.orderLines?.[0]?.salesOrderId;
      const orderLineIdWithVersion = subscriptionData?.orderLineIdWithVersion;

      if (salesOrderId) {
        updateCache(
          cache,
          {
            __typename: 'UnifiedPermit',
            id: salesOrderId,
            orderLineIdWithVersion,
          },
          {
            fragment: gql`
              fragment UnifiedPermitFragment2 on UnifiedPermit {
                creditCardId
                renewal
              }
            `,
            data: {
              creditCardId: data?.updateSalesOrder?.creditCardId,
              renewal: data?.updateSalesOrder?.renewal,
            },
          }
        );
      }
    },
    customOnCompleted: () => {
      showToast(t('success.updateSubscription'), 'success');
    },
    customOnError: () => {
      showToast(t('error.updatePermit'), 'error');
    },
  });

  useEffect(() => {
    setIsRecurring(!!subscriptionData?.renewal);
    setNextBillingDate(subscriptionData?.nextBillingDate ?? null);
  }, [subscriptionData, setIsRecurring, setNextBillingDate]);

  const purchaseSubscription = async () => {
    await createDraftSalesOrder({
      pmcId: availableSubscription?.pmcId ?? '',
      selectedPmc: availableSubscription as Pmc,
      startDate: new Date(),
      billingType: getBillingType(availableSubscription?.billingTypes ?? []),
      type: 'AIMO_PLUS',
      automaticOnlineOrderActivation: true,
      isFirstMonthFree: availableSubscription?.isFirstMonthFree ?? false,
      countryCode: availableSubscription?.countryCode,
      prices: availableSubscription?.prices ?? [],
    });
  };

  return {
    subscription: subscriptionData,
    purchaseSubscription,
    refetchUserSubscriptions: refetchCustomerSubscription,
    updateSalesOrder,
    updateLoading,
    loadingSalesOrder,
    loadingSubscription:
      loadingAvailableSubscriptions || customerSubscriptionLoading,
  };
};

export default useSubscriptions;
